import { useMemo, useEffect, useRef, useState, useEffectEvent } from 'react';
import { useParams } from 'react-router';
import { useSpring, animated } from 'react-spring';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Flex,
  Form,
  Modal,
  Select,
  Spin,
  Table,
  Typography,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import AgentScript from 'components/AgentScript/AgentScript';
import CondensedTable from 'components/CondensedTable/CondensedTable';
import SectionMessage from 'components/SectionMessage/SectionMessage';
import { UPDATE_DUE_DATE } from 'mutations/dueDate';
import { CANCEL_RECURRING_PAYMENT } from 'mutations/payments';
import { VALIDATE_SCHEDULE_CYCLE_DAY } from 'queries/cycleDay';
import { GET_AUTOPAY_STATUS } from 'queries/account';
import { SIZE, STATUS } from 'utilities/constants';
import { formatDate, getOrdinalSuffix } from 'utilities/datesAndTimes';
import useTranslation from 'hooks/useTranslation';

const { Text, Paragraph } = Typography;

const DUE_DATE_CHANGE_COLUMNS = [
  {
    title: '',
    dataIndex: 'rowTitle',
    rowScope: 'row',
  },
  {
    title: 'Cycle Date',
    render: (data) => {
      return (
        <span data-testid={`cycle-date-${data.testIdTitle}`}>
          {data.cycleDate || '-'}
        </span>
      );
    },
  },
  {
    title: 'Statement Period',
    render: (data) => {
      return (
        <span data-testid={`statement-period-${data.testIdTitle}`}>
          {data.statementPeriod || '-'}
        </span>
      );
    },
  },
  {
    title: 'Due Date',
    render: (data) => {
      return (
        <span data-testid={`due-date-${data.testIdTitle}`}>
          {data.dueDate || '-'}
        </span>
      );
    },
  },
];

const getCycleData = (
  currentDueDate,
  selectedDueDate,
  statementStartDate,
  statementEndDate,
) => {
  const currentStatementPeriod = `${formatDate(statementStartDate)} - ${formatDate(statementEndDate)}`;
  const currentCycleDate = dayjs(statementEndDate);

  const nextStatementDueDate = dayjs(currentDueDate).add(1, 'month');
  let proposedDueDate, proposedCycleDate, proposedStatementPeriod;

  if (selectedDueDate) {
    proposedDueDate = nextStatementDueDate.date(selectedDueDate);

    const selectedDateIsSooner = proposedDueDate.isBefore(nextStatementDueDate);

    if (selectedDateIsSooner) {
      proposedDueDate = proposedDueDate.add(1, 'month');
    }

    proposedCycleDate = proposedDueDate.add(3, 'day').subtract(1, 'month');
    proposedStatementPeriod = `${formatDate(statementStartDate)} - ${formatDate(proposedCycleDate)}`;
  }

  return [
    {
      rowTitle: 'Current',
      testIdTitle: 'current',
      cycleDate: currentCycleDate.date(),
      statementPeriod: currentStatementPeriod,
      dueDate: formatDate(nextStatementDueDate),
    },
    {
      rowTitle: 'After Change',
      testIdTitle: 'after',
      cycleDate: proposedCycleDate?.date(),
      statementPeriod: proposedStatementPeriod,
      dueDate: formatDate(proposedDueDate),
    },
  ];
};

const DueDateChangeModal = ({
  dueDate,
  dueDateChangeEligibility,
  loadingDueDate,
  statementStartDate,
  statementEndDate,
  showAutopayModal,
  hide,
  open,
  ...modalProps
}) => {
  const { accountId, customerId } = useParams();
  const { currentLanguage } = useTranslation();

  const [dueDateForm] = Form.useForm();
  const selectedDueDate = Form.useWatch('dueDate', dueDateForm);
  const openRef = useRef(open);

  const [showCoverSuccess, setShowCoverSuccess] = useState(false);
  const [showUpdateSuccess, setShowUpdateSuccess] = useState(false);
  const [showUpdateFailure, setShowUpdateFailure] = useState(false);
  const [showCycleValidationFailure, setShowCycleValidationFailure] =
    useState(false);

  const [showCancelAutopaySuccess, setShowCancelAutopaySuccess] =
    useState(false);
  const [showCancelAutopayFailure, setShowCancelAutopayFailure] =
    useState(false);

  const [cycleValidation, setCycleValidation] = useState();
  const hasAutopayConflict =
    cycleValidation && cycleValidation.isValid === false;

  const slideDownScript = useSpring({
    opacity: hasAutopayConflict ? 1 : 0,
    maxHeight: hasAutopayConflict ? '200px' : '0px',
    config: { tension: 50, friction: 15 },
  });

  const displayAlerts =
    showUpdateSuccess ||
    showUpdateFailure ||
    showCancelAutopaySuccess ||
    showCancelAutopayFailure;

  const autopayQuery = useQuery(GET_AUTOPAY_STATUS, {
    variables: { customerId, accountId },
    skip: !open || !customerId || !accountId,
  });

  const [validateCycleDay, validateCycleDayQuery] = useLazyQuery(
    VALIDATE_SCHEDULE_CYCLE_DAY,
    {
      fetchPolicy: 'no-cache',
    },
  );

  const { currentDueDate, dueDateOptions } = useMemo(() => {
    const current = dayjs(dueDate);
    const options = [];

    if (current.isValid()) {
      // always block the 26th, 27th and 28th
      const blockedDates = [current.date(), 26, 27, 28];
      // block the four days before the current due date
      for (let day = 1; day <= 4; day++) {
        const blockedDate = current.subtract(day, 'day');
        // only return the day of the month
        blockedDates.push(blockedDate.date());
      }

      for (let day = 1; day <= 28; day++) {
        if (!blockedDates.includes(day)) {
          options.push({
            label: `${day}`,
            value: day,
          });
        }
      }
    }

    return { currentDueDate: current, dueDateOptions: options };
  }, [dueDate]);

  const [updateDueDate, updateDueDateMutation] = useMutation(UPDATE_DUE_DATE, {
    refetchQueries: ['GetCustomerWithFullAccounts'],
    onQueryUpdated: async (observableQuery) => {
      //delay refetch of account data so the success message has time to display
      const delayRefetch = async () => {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        observableQuery.refetch();
      };
      delayRefetch();
    },
  });

  const [cancelAutopay, cancelAutopayMutation] = useMutation(
    CANCEL_RECURRING_PAYMENT,
    {
      refetchQueries: ['AutopayQuery', 'GetAutopayStatus'],
    },
  );

  const [currentCycle, proposedCycle] = getCycleData(
    currentDueDate,
    selectedDueDate,
    statementStartDate,
    statementEndDate,
  );

  const proposedCycleDay = proposedCycle?.cycleDate;

  const autopayData = autopayQuery.data?.account?.autopay;
  const autopayScheduleId = autopayData?.length ? autopayData[0].id : undefined;

  const loadingAutopayInfo = autopayQuery.loading;
  const validatingCycleDay = validateCycleDayQuery.loading;
  const updatesProcessing =
    updateDueDateMutation.loading || cancelAutopayMutation.loading;

  const handleClose = (proceedToAutopay = false) => {
    hide && hide();
    setShowCoverSuccess(false);
    setShowUpdateSuccess(false);
    setShowUpdateFailure(false);
    setShowCancelAutopaySuccess(false);
    setShowCancelAutopayFailure(false);
    setShowCycleValidationFailure(false);
    setCycleValidation(undefined);
    dueDateForm.resetFields();
    if (proceedToAutopay) {
      showAutopayModal && showAutopayModal();
    }
  };

  const handleSubmit = async () => {
    setShowUpdateFailure(false);

    let formValues;
    try {
      formValues = await dueDateForm.validateFields();
    } catch {
      return;
    }

    try {
      await updateDueDate({
        variables: { accountId, newDueDateDay: formValues.dueDate },
      });
    } catch (error) {
      setShowUpdateFailure(true);
      return;
    }

    if (hasAutopayConflict) {
      setShowUpdateSuccess(true);
    } else {
      setShowCoverSuccess(true);
      setTimeout(handleClose, 2000);
      return;
    }

    try {
      await cancelAutopay({
        variables: { customerId, scheduleId: autopayScheduleId },
      });
      setShowCancelAutopaySuccess(true);
    } catch {
      setShowCancelAutopayFailure(true);
    }

    dueDateForm.resetFields();
    setCycleValidation(undefined);
  };

  const renderAutopayValidation = () => {
    let alertType = 'info';
    let message = (
      <span data-testid="no-autopay-message">
        Customer not enrolled in autopay. <br /> All due dates are eligible.
      </span>
    );

    if (autopayScheduleId) {
      alertType = 'warning';
      message = (
        <span data-testid="has-autopay-message">
          Customer is enrolled in autopay.
          <br />
          <strong>New due date requires validation.</strong>
        </span>
      );
    }

    if (cycleValidation && cycleValidation.isValid) {
      alertType = 'success';
      message = (
        <span data-testid="no-conflict-message">
          No autopay schedule conflict.
        </span>
      );
    }

    if (cycleValidation && !cycleValidation.isValid) {
      alertType = 'warning';
      message = (
        <span data-testid="has-conflict-message">
          Autopay scheduling conflict.
          <br />
          <strong>Autopay will be cancelled on submit.</strong>
        </span>
      );
    }

    if (showCycleValidationFailure) {
      alertType = 'error';
      message = (
        <span data-testid="cycle-validation-failure-message">
          Failed to validate autopay schedule.
        </span>
      );
    }

    if (autopayQuery.error) {
      alertType = 'error';
      message = (
        <span data-testid="autopay-failure-message">
          Failed to load autopay schedule.
        </span>
      );
    }

    return validatingCycleDay || loadingAutopayInfo ? (
      <Flex
        align="center"
        justify="center"
        style={{
          flex: '1',
          background: 'var(--color-fill-quaternary)',
        }}
      >
        <Spin
          indicator={<LoadingOutlined />}
          description={
            validatingCycleDay
              ? 'Validating Autopay Schedule'
              : 'Loading Autopay Info'
          }
        />
      </Flex>
    ) : (
      <Alert
        banner
        type={alertType}
        style={{
          flex: '1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
        title={message}
      ></Alert>
    );
  };

  const validateProposedCycleDateAgainstAutopaySchedule = useEffectEvent(
    async (proposedCycleDay, autopayScheduleId) => {
      setShowCycleValidationFailure(false);
      const response = await validateCycleDay({
        variables: {
          proposedCycleDay,
          scheduleId: autopayScheduleId,
        },
      });

      if (response.error) {
        setCycleValidation(undefined);
        setShowCycleValidationFailure(true);
      } else {
        setCycleValidation(response.data?.validateScheduleCycleDay);
      }
    },
  );

  useEffect(() => {
    if (autopayScheduleId && proposedCycleDay) {
      validateProposedCycleDateAgainstAutopaySchedule(
        proposedCycleDay,
        autopayScheduleId,
      );
    }
  }, [proposedCycleDay, autopayScheduleId]);

  useEffect(() => {
    if (openRef.current === true && !open) {
      handleClose();
    }
    openRef.current = open;
  }, [open]);

  return (
    <Modal
      {...modalProps}
      open={open}
      centered
      width={700}
      title="Due Date Change"
      onOk={handleSubmit}
      okText="Submit"
      okButtonProps={{
        loading: updateDueDateMutation.loading || cancelAutopayMutation.loading,
        hidden: !dueDateChangeEligibility?.isEligible,
        disabled: validatingCycleDay || loadingAutopayInfo,
      }}
    >
      {dueDateChangeEligibility?.isEligible ? (
        <>
          <Alert
            banner
            data-testid="statement-period-warning"
            type="warning"
            title={
              <span>
                <strong>Note</strong>: The upcoming due date for this account,{' '}
                <strong>{formatDate(currentDueDate)}</strong>,{' '}
                <strong>will not change</strong> as a result of completing this
                due date change request.
              </span>
            }
            style={{ marginBottom: 'var(--spacing-lg)' }}
          />

          <Form form={dueDateForm} layout="vertical">
            <Flex
              flex="1"
              gap="var(--spacing-sm)"
              style={{
                marginBottom: 'var(--spacing-md)',
                border: 'var(--border-secondary)',
                borderRadius: 'var(--border-radius)',
                padding: 'var(--spacing-sm)',
              }}
            >
              <Form.Item
                label="Select new due date"
                name="dueDate"
                style={{ width: '200px', marginBottom: 0 }}
                rules={[
                  { required: true, message: 'Please select a due date' },
                ]}
              >
                <Select
                  data-testid="due-date-select"
                  disabled={
                    loadingAutopayInfo ||
                    validatingCycleDay ||
                    showUpdateSuccess
                  }
                  options={dueDateOptions}
                  style={{ maxWidth: '200px' }}
                />
              </Form.Item>
              {renderAutopayValidation()}
            </Flex>
          </Form>
          <animated.div style={{ ...slideDownScript, overflow: 'hidden' }}>
            <AgentScript
              title="Autopay Scheduling Conflict"
              translateKey={`payments.dueDateConflict`}
              values={{
                newDueDate:
                  currentLanguage === 'en'
                    ? getOrdinalSuffix(selectedDueDate)
                    : selectedDueDate,
              }}
            />
          </animated.div>

          <CondensedTable
            data={[currentCycle, proposedCycle]}
            columns={DUE_DATE_CHANGE_COLUMNS}
            bordered={true}
            loading={loadingDueDate}
            size="small"
          />
          <SectionMessage
            status={STATUS.SUCCESS}
            size={SIZE.SM}
            text="Due date successfully updated"
            visible={showCoverSuccess}
            cover={true}
          />

          {!updatesProcessing && displayAlerts && (
            <Flex
              vertical
              gap="var(--spacing-xs)"
              style={{ marginTop: 'var(--spacing-md)' }}
            >
              {showUpdateFailure && (
                <Alert
                  banner
                  type="error"
                  title="Failed to update due date. Please try again."
                />
              )}
              {showUpdateSuccess && (
                <Alert
                  banner
                  data-testid="due-date-update-success"
                  type="success"
                  title="Due date successfully updated."
                />
              )}
              {showCancelAutopayFailure && (
                <Alert
                  banner
                  type="error"
                  title={
                    <span>
                      Failed to cancel autopay. Please handle cancellation and
                      updating of autopay manually. <br />
                      <a role="button" onClick={() => handleClose(true)}>
                        Proceed to autopay management.
                      </a>
                    </span>
                  }
                />
              )}

              {showCancelAutopaySuccess && (
                <Alert
                  banner
                  data-testid="autopay-cancel-success"
                  type="success"
                  title={
                    <div>
                      Autopay successfully canceled. Encourage the customer to
                      renew autopay inline with their new due date. <br />
                      <a role="button" onClick={() => handleClose(true)}>
                        Proceed to autopay management.
                      </a>
                    </div>
                  }
                />
              )}
            </Flex>
          )}
        </>
      ) : (
        <EligibilityCriteria
          dueDateChangeEligibility={dueDateChangeEligibility}
        />
      )}
    </Modal>
  );
};

const EligibilityCriteria = ({ dueDateChangeEligibility }) => {
  const criteria = Object.assign({}, dueDateChangeEligibility);

  const dataSource = [
    {
      key: 1,
      name: 'Account is not Delinquent',
      status: criteria.isNotDelinquent,
    },
    {
      key: 2,
      name: 'Account is not Charged Off',
      status: criteria.isNotChargedOff,
    },
    {
      key: 3,
      name: 'Account is not labeled Bankrupt',
      status: criteria.isNotBankrupt,
    },
    {
      key: 4,
      name: 'Account has 2 or less prior due date changes',
      status: criteria.hasTwoOrLessDueDateChanges,
    },
    {
      key: 5,
      name: 'Account has no due date changes in the last 6 months',
      status: criteria.hasNoChangeInPastSixMonths,
    },
    {
      key: 6,
      name: 'Account passes fraud validation',
      status: criteria.hasPassedFraudValidation,
    },
    {
      key: 7,
      name: 'Account is not Within 0% Intro Rate Period',
      status: criteria.isNotWithinIntroAprPeriod,
    },
    {
      key: 8,
      name: 'Account is past first cycle',
      status: criteria.isPastFirstCycle,
    },
    {
      key: 9,
      name: 'Account does not have an Upcoming CIT',
      status: criteria.hasNoUpcomingCit,
    },
  ];

  const columns = [
    {
      dataIndex: 'name',
      key: 'name',
      render: (name) => <Typography.Text strong>{name}</Typography.Text>,
    },
    {
      dataIndex: 'status',
      key: 'status',
      width: '10%',
      render: (status) => {
        return (
          <Typography.Text strong type={status ? 'success' : 'danger'}>
            {status ? 'Pass' : 'Fail'}
          </Typography.Text>
        );
      },
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 'var(--spacing)' }}>
        <Paragraph strong data-testid="ineligibility-message">
          Eligibility: <Text type="danger">Ineligible</Text>
        </Paragraph>
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        bordered={true}
        size="small"
        pagination={false}
        showHeader={false}
        style={{ marginBottom: 'var(--spacing)' }}
      />
    </>
  );
};

export default DueDateChangeModal;
